import { useEffect, useRef, useState } from "react";

/**
 * Auto-saves an in-progress form to localStorage so an accidental Back tap
 * doesn't lose the user's work.
 *
 * Usage:
 *   const { form, setForm, restoredAt, discardDraft, clearDraft } =
 *     useFormDraft("ica_draft_character", emptyForm, { enabled: !isEdit });
 *
 *   - `enabled: false` disables the whole thing (e.g. edit mode where the
 *     server-provided data must not be overwritten by a stale draft).
 *   - Call `clearDraft()` after a successful submit.
 *   - `discardDraft()` clears the draft AND resets the form to `initial`.
 *   - `restoredAt` is a Date if a draft was found on mount, else null.
 *
 * Writes are debounced (250ms) so keystrokes don't hammer localStorage.
 */
export default function useFormDraft(key, initial, { enabled = true } = {}) {
  const [form, setForm] = useState(initial);
  const [restoredAt, setRestoredAt] = useState(null);
  const timerRef = useRef(null);
  const bootstrappedRef = useRef(false);

  // Restore on mount (only when enabled).
  useEffect(() => {
    if (!enabled) { bootstrappedRef.current = true; return; }
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.form && parsed.savedAt) {
          setForm({ ...initial, ...parsed.form });
          setRestoredAt(new Date(parsed.savedAt));
        }
      }
    } catch {}
    bootstrappedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  // Persist on change (debounced). Skip until initial restore has run.
  useEffect(() => {
    if (!enabled || !bootstrappedRef.current) return;
    // Never persist a completely-empty form — nothing to restore anyway.
    const hasContent = Object.values(form).some((v) => {
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "boolean") return false;  // toggles don't count
      return !!(v && String(v).trim());
    });
    if (!hasContent) {
      localStorage.removeItem(key);
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify({ form, savedAt: Date.now() }));
      } catch {}
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [form, key, enabled]);

  const clearDraft = () => { try { localStorage.removeItem(key); } catch {} };
  const discardDraft = () => { clearDraft(); setForm(initial); setRestoredAt(null); };

  return { form, setForm, restoredAt, clearDraft, discardDraft };
}
